/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { Box, Breadcrumbs, FormControl, IconButton, InputAdornment, Select, Tooltip, Typography } from "@mui/material";
import { StyledBreadCrumbContainer, StyledInputLabel, StyledLink } from "../employee_visibility/styles";
import { MenuHeading } from "../../../components/menu_heading";
import { selectBaseStyles, viewTypographyStyles } from "../styles";
import { IndexTable } from "../../../components/index_table";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import api from "../../../api/index.js";
import authenticatedFetch from "../../../api/authenticatedFetch";
import camelize from "../../../utils/camelize.js";
import { Loader } from "../../../components/loader";
import DropdownSearchBar from "../../../components/dorpdown_searchbar/index.jsx";
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import { UpdateButton } from "../../../components/update_button/index.jsx";
import { ResetButton } from "../../../components/reset_button";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { EmployeePastVisitsModal } from "../../../components/employee_past_visits_modal/index.jsx";
import ClearIcon from '@mui/icons-material/Clear';
import { SelectedMenuItem } from '../../../components/selected_menu_item/index'
import {
  LocalizationProvider,
  DatePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { datePickerStyles } from "./styles.js";
import { buildQueryParams } from "../../../utils/buildQueryParams.js";
import { useNotificationContext } from "../../../contexts/notificationContext.jsx";
import { FilterContainer } from "../../../components/filter_container/index.jsx";
import { StyledMenuItem } from "../../../components/styled_menu_item/index.jsx";
import { hasSingleItem } from "../../../utils/hasSingleItem.js";
import { getSingleItemValue } from "../../../utils/getSingleItemValue.js";
import { Query_Keys } from "../../../lib/queryKeys.js";
import { PerPage } from "../../../constants/perPage";
import {
  getDropdownNextPageParam,
  mergeUniqueByKey,
  normalizeEmployee,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../../utils/dropdownInfiniteQuery";
import { useDebounce } from "../../../utils/useDebounce";
import { useInfiniteScrollObserver } from "../../../hooks/useInfiniteScrollObserver";
import { isValidDayjsValue } from "../../../utils/isValidDayjsValue";
import { isValidCustomDateRange } from "../../../utils/isValidCustomDateRange";

const ALL_VALUE = 'All';
const defaultFilter = {
  dateFilter: ALL_VALUE,
  network_guest_id: ALL_VALUE,
  restaurant_group_id: ALL_VALUE,
  restaurant_ids: [ALL_VALUE],
  customDateFrom: null,
  customDateTo: null,
}

const EmployeeInsights = () => {
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(defaultFilter);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilter)
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [open, setOpen] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState('')
  const [restaurantSearch, setRestaurantSearch] = useState('')
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState('')
  const [csvLoading, setCsvLoading] = useState(false)
  const [sortBy, setSortBy] = useState("id")
  const [sortOrder, setSortOrder] = useState("desc")
  const showNotification = useNotificationContext();
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([])
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([])
  const [employeeOptionsCache, setEmployeeOptionsCache] = useState([])
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300)
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300)
  const debouncedEmployeeSearch = useDebounce(employeeSearch.trim(), 300)

  const Days = useMemo(() => [
    "Today",
    "Yesterday",
    "This Week",
    "Last 7 Days",
    "This Month",
    "Last Month",
    "Custom Date",
  ], [])

  const formattedFilters = (sourceFilters = appliedFilters) => {
    const params = {};
    const today = dayjs();

    // Date range handling
    if (sourceFilters?.dateFilter !== "Custom Date" && sourceFilters?.dateFilter !== ALL_VALUE) {
      switch (sourceFilters.dateFilter) {
        case "Today":
          params.date_range = {
            from: today.format("DD/MM/YYYY"),
            to: today.format("DD/MM/YYYY"),
          };
          break;
        case "Last 7 Days":
          params.date_range = {
            from: today.subtract(7, "day").format("DD/MM/YYYY"),
            to: today.format("DD/MM/YYYY"),
          };
          break;
        case "Yesterday":
          params.date_range = {
            from: today.subtract(1, "day").format("DD/MM/YYYY"),
            to: today.subtract(1, "day").format("DD/MM/YYYY"),
          };
          break;
        case "This Week":
          params.date_range = {
            from: today.startOf("week").format("DD/MM/YYYY"),
            to: today.endOf("week").format("DD/MM/YYYY"),
          };
          break;
        case "This Month":
          params.date_range = {
            from: today.startOf("month").format("DD/MM/YYYY"),
            to: today.endOf("month").format("DD/MM/YYYY"),
          };
          break;
        case "Last Month":
          params.date_range = {
            from: today
              .subtract(1, "month")
              .startOf("month")
              .format("DD/MM/YYYY"),
            to: today.subtract(1, "month").endOf("month").format("DD/MM/YYYY"),
          };
          break;
      }
    } else if (sourceFilters?.dateFilter === "Custom Date" && sourceFilters?.customDateFrom && sourceFilters.customDateTo) {
      params.date_range = {
        from: dayjs(sourceFilters.customDateFrom).format("DD/MM/YYYY"),
        to: dayjs(sourceFilters.customDateTo).format("DD/MM/YYYY"),
      };
    }

    if (sourceFilters?.network_guest_id !== ALL_VALUE) {
      params.network_guest_id = sourceFilters.network_guest_id;
    }

    if (!sourceFilters?.restaurant_ids?.includes(ALL_VALUE)) {
      params.restaurant_ids = sourceFilters.restaurant_ids;
    }

    if (sourceFilters?.restaurant_group_id !== ALL_VALUE) {
      params.restaurant_group_id = sourceFilters.restaurant_group_id;
    }

    return params;
  }

  const fetchEmployeeInsights = async () => {
    const data = formattedFilters(appliedFilters)
    const response = await api.get("/v1/admin/employees/insights", {
      params: { per_page: itemsPerPage, page: page, sort_by: sortBy, sort_order: sortOrder, ...data },
    });
    return { data: response?.data, pageCount: response?.headers["total-count"] }
  }

  const { data: insights, isFetching } = useQuery({
    queryKey: Query_Keys.insights({
      queryFilter: appliedFilters,
      page: page,
      itemsPerPage: itemsPerPage,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchEmployeeInsights,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const fetchRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    });
    return response?.data?.map(normalizeRestaurantGroup) || [];
  };

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({ search: debouncedRestaurantGroupSearch }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchRestaurants = async ({ pageParam = 1 }) => {
    let res = await api.get("/v1/admin/dropdowns/restaurants_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
        restaurant_group_ids: filters?.restaurant_group_id === ALL_VALUE ? undefined : filters?.restaurant_group_id
      }
    });
    return res?.data?.map(normalizeRestaurant) || [];
  };

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      search: debouncedRestaurantSearch,
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: true
  });

  const fetchEmployeeList = async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/active_guests_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedEmployeeSearch || undefined,
      },
    })
    return response?.data?.map(normalizeEmployee) || []
  }

  const {
    data: employeesList = [],
    isFetching: employeeFetching,
    isFetchingNextPage: employeesFetchingNextPage,
    fetchNextPage: fetchNextEmployeesPage,
    hasNextPage: hasNextEmployeesPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.active_guests_list({ search: debouncedEmployeeSearch }),
    queryFn: fetchEmployeeList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  })

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  })

  const { rootRef: employeeMenuRef, targetRef: employeeLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextEmployeesPage,
    hasNextPage: hasNextEmployeesPage,
    isFetchingNextPage: employeesFetchingNextPage,
  })

  useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurants, 'id'));
  }, [restaurants]);

  useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurantGroups, 'label'));
  }, [restaurantGroups]);

  useEffect(() => {
    setEmployeeOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, employeesList, 'value'));
  }, [employeesList]);

  const hasSingleAvailableRestaurantGroup = !debouncedRestaurantGroupSearch && hasSingleItem(restaurantGroups);
  const hasSingleAvailableRestaurant = !debouncedRestaurantSearch && hasSingleItem(restaurants);
  const hasSingleAvailableEmployee = !debouncedEmployeeSearch && hasSingleItem(employeesList);

  useEffect(() => {
    if (hasSingleAvailableRestaurantGroup) {
      const singleRestaurantGroup = getSingleItemValue(restaurantGroups);
      setFilters(prev => ({
        ...prev,
        restaurant_group_id: singleRestaurantGroup.label,
        restaurant_ids: [ALL_VALUE],
      }));
    }
  }, [hasSingleAvailableRestaurantGroup, restaurantGroups]);

  useEffect(() => {
    if (hasSingleAvailableRestaurant) {
      const singleRestaurant = getSingleItemValue(restaurants);
      setFilters(prev => ({
        ...prev,
        restaurant_ids: [singleRestaurant.id]
      }));
    }
  }, [hasSingleAvailableRestaurant, restaurants]);

  useEffect(() => {
    if (hasSingleAvailableEmployee) {
      const singleEmployee = getSingleItemValue(employeesList);
      setFilters(prev => ({
        ...prev,
        network_guest_id: singleEmployee.value
      }));
    }
  }, [employeesList, hasSingleAvailableEmployee]);

  const getRestaurantGroupName = useCallback((id) => {
    if (id === ALL_VALUE) return 'All';
    const restaurantGroup = restaurantGroupOptionsCache?.find((res) => res.label === id)
    return restaurantGroup?.value ? restaurantGroup?.value : 'All'
  }, [restaurantGroupOptionsCache])

  const getEmployeeName = useCallback((id) => {
    if (id === ALL_VALUE) return 'All';
    const employee = employeeOptionsCache.find((employee) => employee.value === id)
    return employee ? employee?.label : ''
  }, [employeeOptionsCache])

  const handleEmployeeChange = (event) => {
    setFilters({ ...filters, network_guest_id: event.target.value });
    setFiltersChanged(true);
  }

  const handleRestaurantChange = (event) => {
    const {
      target: { value },
    } = event;
    setFiltersChanged(true);

    if ((value?.length === 1 && value.includes(ALL_VALUE)) || value?.length === 0) {
      setFilters({ ...filters, restaurant_ids: [ALL_VALUE] });
    }
    else {
      const newRestaurantIds = value.filter(val => val !== ALL_VALUE);
      setFilters({ ...filters, restaurant_ids: newRestaurantIds });
    }
  }

  const handleRestaurantGroupChange = (event) => {
    setFilters({ ...filters, restaurant_group_id: event.target.value, restaurant_ids: [ALL_VALUE] })
    setFiltersChanged(true)
  }

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      if (field === "dateFilter") {
        return {
          ...prev,
          dateFilter: value,
          customDateFrom: null,
          customDateTo: null,
        };
      }

      if (field === "customDateFrom") {
        if (!value) {
          return {
            ...prev,
            customDateFrom: null,
            customDateTo: null,
          };
        }

        return {
          ...prev,
          customDateFrom: value,
          customDateTo:
            isValidDayjsValue(value) && !isValidDayjsValue(prev.customDateTo)
              ? dayjs().startOf("day")
              : prev.customDateTo,
        };
      }

      return { ...prev, [field]: value };
    });
    setFiltersChanged(true);
  };

  const handleRestaurantClear = () => {
    setFilters({ ...filters, restaurant_ids: [ALL_VALUE] });
    setFiltersChanged(true)
  }

  const handleRestaurantGroupClear = () => {
    setFilters({ ...filters, restaurant_group_id: ALL_VALUE, restaurant_ids: [ALL_VALUE] })
    setFiltersChanged(true)
  }

  const handleEmployeeClear = () => {
    setFilters({ ...filters, network_guest_id: ALL_VALUE });
    setFiltersChanged(true)
  }

  const handleDateRangeClear = () => {
    setFilters({
      ...filters,
      dateFilter: ALL_VALUE,
      customDateFrom: null,
      customDateTo: null,
    });
    setFiltersChanged(true)
  }

  const isCustomDateRangeValid =
    filters?.dateFilter !== "Custom Date" ||
    isValidCustomDateRange(filters?.customDateFrom, filters?.customDateTo);

  const isUpdateDisabled =
    filters?.dateFilter === "Custom Date" &&
    !isCustomDateRangeValid;

  const handleUpdate = () => {
    setFiltersChanged(false)
    setAppliedFilters(filters)
    setItemsPerPage(25);
    setPage(1);
  };

  const handleSort = useCallback((column, direction) => {
    setSortBy(column);
    setSortOrder(direction);
    setPage(1);
  }, []);

  const handleReset = () => {
    setFiltersChanged(false)
    setAppliedFilters(defaultFilter)
    setFilters(defaultFilter)
    setItemsPerPage(25);
    setPage(1);
  }

  const getRestaurantDisplayValue = () => {
    if (!filters?.restaurant_ids || filters?.restaurant_ids?.length === 0) {
      return 'All';
    }

    if (filters?.restaurant_ids?.includes(ALL_VALUE)) {
      return 'All';
    }

    if (filters?.restaurant_ids?.length === 1 && restaurantOptionsCache) {
      const restaurant = restaurantOptionsCache.find(r => r.id === filters.restaurant_ids[0]);
      return restaurant ? restaurant.name : 'All';
    }

    return `${filters?.restaurant_ids?.length} Restaurants Selected`;
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleEmployee = (data) => {
    setOpen(true)
    setSelectedEmployee(data)
  }

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true);
      const filters = formattedFilters(appliedFilters)
      const query = buildQueryParams(filters)
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=insights&${query}`;
      console.log(url)

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      let date = new Date
      let filename = `employee_insights_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('CSV download error:', error);
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCsvLoading(false);
    }
  }

  const columnOptions = useMemo(() => [
    { key: "id", sortable: true },
    { key: "employee_name", sortable: true },
    { key: "phone_number", sortable: true },
    { key: "saved_total", sortable: true },
    { key: "total_orders_value", sortable: true },
    { key: "total_visits", sortable: true },
    { key: "past_visits", sortable: false }
  ], []);

  const columns = useMemo(() =>
    columnOptions?.map((column) => ({
      key: column.key,
      label: camelize(column.key),
      sortable: column.sortable
    })), [columnOptions]
  );

  const rows = useMemo(() => {
    if (!insights?.data) return [];

    return insights.data.map((record) => ({
      id: record?.id,
      employee_name: record?.name,
      phone_number: record?.phone_number,
      saved_total: `$${Number(record?.saved_total).toFixed(2) ?? '0.00'}`,
      total_orders_value: `$${Number(record?.total_orders_value).toFixed(2) ?? '0.00'}`,
      total_visits: record?.total_visits,
      past_visits: (
        <Tooltip
          title="View"
          arrow
          onClick={() => handleEmployee(record)}
        >
          <IconButton>
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      )
    }));
  }, [insights?.data]);

  return (
    <React.Fragment>
      <Box sx={{
        height: "100%",
        width: "100%",
        overflowY: 'scroll',
        overflowX: 'hidden',
      }}
      >
        <StyledBreadCrumbContainer>
          <Breadcrumbs>
            <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
            <StyledLink color={"#515152"}>Reports</StyledLink>
            <StyledLink color={"#515152"}>Employee Reports</StyledLink>
            <StyledLink color={"#515152"}>Employee Insights</StyledLink>
          </Breadcrumbs>
        </StyledBreadCrumbContainer>

        <FilterContainer data-testid="orders-modifiers-filter-section">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: { xs: 2, md: 0 }
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: "18px" },
                flexWrap: "wrap",
                width: { xs: "100%", md: "auto" },
                flex: 1
              }}
            >
              <React.Fragment>
                <Typography
                  variant="body2"
                  sx={{
                    ...viewTypographyStyles,
                    alignSelf: { xs: "flex-start", sm: "center" },
                    whiteSpace: "nowrap"
                  }}
                >
                  View
                </Typography>

                <FormControl
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: "160px" }
                  }}
                >
                  <StyledInputLabel>Restaurant Group</StyledInputLabel>
                  <Select
                    size="small"
                    label="Restaurant Group"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { ref: restaurantGroupMenuRef, sx: { maxHeight: "40vh" } },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: { xs: "100%", sm: "160px" },
                      width: { xs: "100%", sm: "160px" },
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleRestaurantGroupChange}
                    onClose={() => setTimeout(() => setRestaurantGroupSearch(''), 1000)}
                    value={hasSingleAvailableRestaurantGroup
                      ? getSingleItemValue(restaurantGroups)?.label
                      : (filters?.restaurant_group_id || ALL_VALUE)}
                    renderValue={() =>{
                      if (hasSingleAvailableRestaurantGroup) {
                        return getSingleItemValue(restaurantGroups)?.value || 'All';
                      }
                      return getRestaurantGroupName(filters?.restaurant_group_id);
                    }}
                    endAdornment={
                      filters?.restaurant_group_id !== ALL_VALUE && !hasSingleAvailableRestaurantGroup ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleRestaurantGroupClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        null
                      )
                    }
                  >
                    <DropdownSearchBar changeSearchText={setRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={restaurantGroupFetching} />
                    {!hasSingleAvailableRestaurantGroup && (<StyledMenuItem
                      value="All"
                    >
                      {!filters?.restaurant_group_id ? (
                        <SelectedMenuItem data={'All'} />
                      ) : (
                        'All'
                      )}
                    </StyledMenuItem>
                    )}
                    {
                      restaurantGroups
                        .map((res) => (
                          <StyledMenuItem
                            key={res?.label}
                            value={res?.label}
                          >
                            {
                              hasSingleAvailableRestaurantGroup || filters?.restaurant_group_id === res?.label ? <SelectedMenuItem data={res?.value} /> : res?.value
                            }
                          </StyledMenuItem>
                        ))
                    }
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextRestaurantGroupsPage}
                      targetRef={restaurantGroupLoaderRef}
                    />
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: "200px" }
                  }}
                >
                  <StyledInputLabel>Restaurant</StyledInputLabel>
                  <Select
                    size="small"
                    multiple
                    label="Restaurant"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { ref: restaurantMenuRef, sx: { maxHeight: "40vh" } },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: { xs: "100%", sm: "200px" },
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleRestaurantChange}
                    onClose={() => setTimeout(() => setRestaurantSearch(''), 1000)}
                    value={hasSingleAvailableRestaurant
                      ? [getSingleItemValue(restaurants)?.id]
                      : (filters?.restaurant_ids || [ALL_VALUE])}
                    renderValue={() =>  {
                      if (hasSingleAvailableRestaurant) {
                        return getSingleItemValue(restaurants)?.name || 'All';
                      }
                      return getRestaurantDisplayValue();
                    }}
                    endAdornment={
                      !filters?.restaurant_ids?.includes(ALL_VALUE) && !hasSingleAvailableRestaurant ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleRestaurantClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        null
                      )
                    }
                  >
                    <DropdownSearchBar changeSearchText={setRestaurantSearch} placeholder={'Search restaurant'} isFetching={restaurantFetching} />
                    {/* All option */}
                    {!hasSingleAvailableRestaurant && (<StyledMenuItem
                      key={ALL_VALUE}
                      value={ALL_VALUE}
                      disabled={!filters?.restaurant_ids?.includes(ALL_VALUE)}
                    >
                      {filters?.restaurant_ids?.includes(ALL_VALUE) ? <SelectedMenuItem data="All" /> : "All"}
                    </StyledMenuItem>
                    )}
                    {
                      restaurants
                        .map((res) => (
                          <StyledMenuItem
                            key={res?.id}
                            value={res?.id}
                          >
                            {
                              hasSingleAvailableRestaurant ||filters?.restaurant_ids?.includes(res?.id) ? <SelectedMenuItem data={res?.name} /> : res?.name
                            }
                          </StyledMenuItem>
                        ))
                    }
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextRestaurantsPage}
                      targetRef={restaurantLoaderRef}
                    />
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: "200px" }
                  }}
                >
                  <StyledInputLabel>Employees</StyledInputLabel>
                  <Select
                    value={hasSingleAvailableEmployee
                      ? getSingleItemValue(employeesList)?.value
                      : (filters?.network_guest_id || ALL_VALUE)}
                    label="Employees"
                    size="small"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { ref: employeeMenuRef, sx: { maxHeight: "40vh" } },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: { xs: "100%", sm: "200px" },
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleEmployeeChange}
                    onClose={() => setEmployeeSearch("")}
                    renderValue={() =>{
                      if (hasSingleAvailableEmployee) {
                        return getSingleItemValue(employeesList)?.label || 'All';
                      }
                      return getEmployeeName(filters?.network_guest_id);
                    }
                    }
                    endAdornment={
                      filters?.network_guest_id !== ALL_VALUE && !hasSingleAvailableEmployee ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleEmployeeClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        null
                      )
                    }
                  >
                    <DropdownSearchBar changeSearchText={setEmployeeSearch} placeholder={'Search employee'} isFetching={employeeFetching} />
                    {/* All option */}
                    {!hasSingleAvailableEmployee && (<StyledMenuItem
                      value={ALL_VALUE}
                    >
                      {filters?.network_guest_id === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                    </StyledMenuItem>
                    )}
                    {employeesList
                      .map((employee) => (
                        <StyledMenuItem
                          key={employee.value}
                          value={employee.value}
                        >
                          {
                            hasSingleAvailableEmployee || filters?.network_guest_id === employee?.value ? <SelectedMenuItem data={employee?.label} /> : employee?.label
                          }
                        </StyledMenuItem>
                      ))}
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextEmployeesPage}
                      targetRef={employeeLoaderRef}
                    />
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: "160px" }
                  }}
                >
                  <StyledInputLabel>Date Range</StyledInputLabel>
                  <Select
                    value={filters?.dateFilter || ALL_VALUE}
                    renderValue={() =>
                      filters?.dateFilter
                    }
                    label='Date Range'
                    size="small"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { sx: { width: "230px" } },
                      autoFocus: false,
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: { xs: "100%", sm: "160px" },
                      borderColor: "#2b4fb9",
                    }}
                    onChange={(e) => handleFilterChange("dateFilter", e.target.value)}
                    endAdornment={
                      filters?.dateFilter !== ALL_VALUE ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleDateRangeClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        null
                      )
                    }
                  >
                    {/* All option */}
                    <StyledMenuItem
                      value={ALL_VALUE}
                    >
                      {filters?.dateFilter === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                    </StyledMenuItem>
                    {Days.map((value) => (
                      <StyledMenuItem
                        value={value}
                        key={value}
                      >
                        {
                          filters?.dateFilter === value ? (
                            <SelectedMenuItem data={value} />
                          ) : (
                            <>
                              {value}
                            </>
                          )
                        }
                      </StyledMenuItem>
                    ))}
                  </Select>
                </FormControl>

                {filters.dateFilter === "Custom Date" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                      width: { xs: "100%", sm: "auto" }
                    }}
                  >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Typography variant="body2" sx={viewTypographyStyles}>
                        Days
                      </Typography>
                      <DatePicker
                        format="MM-DD-YYYY"
                        sx={{
                          ...datePickerStyles,
                          width: { xs: "100%", sm: "140px", lg: '160px' }
                        }}
                        slotProps={{ textField: { size: "small" } }}
                        value={filters.customDateFrom || null}
                        onChange={(date) =>
                          handleFilterChange("customDateFrom", date)
                        }
                      />
                      <Typography variant="body2" sx={viewTypographyStyles}>
                        through
                      </Typography>
                      <DatePicker
                        sx={{
                          ...datePickerStyles,
                          width: { xs: "100%", sm: "140px", lg: '160px' }
                        }}
                        format="MM-DD-YYYY"
                        slotProps={{ textField: { size: "small" } }}
                        value={filters.customDateTo || null}
                        onChange={(date) =>
                          handleFilterChange("customDateTo", date)
                        }
                      />
                    </LocalizationProvider>
                  </Box>
                )}
              </React.Fragment>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                alignSelf: { xs: "flex-start", md: "center" },
                mt: { xs: 1, md: 0 },
                width: { xs: "100%", md: "auto" },
                justifyContent: { xs: "flex-start", md: "flex-end" }
              }}
            >
              <ResetButton
                onClick={handleReset}
                status={isFetching}
              />

              <UpdateButton
                onClick={handleUpdate}
                filtersChanged={filtersChanged}
                status={isFetching}
                disabled={isUpdateDisabled}
              />
            </Box>
          </Box>
        </FilterContainer>

        <MenuHeading report={false}>Employee Insights Report</MenuHeading>

        {isFetching ? (
          <Loader />
        ) : (
          <IndexTable
            columns={columns}
            data={rows}
            tableId="employee-insights"
            type="entries"
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            pageCount={insights?.pageCount}
            report={false}
            showDownloadButton={true}
            disableDownloadButton={insights?.data?.length === 0 || csvLoading}
            handleExportCsv={handleExportCsv}
            csvLoading={csvLoading}
            serverSort={true}
            onSortChange={handleSort}
            sortBy={sortBy}
            sortOrder={sortOrder}
            defaultSortBy={sortBy}
            defaultSortOrder={sortOrder}
          />
        )}

        {/* Employee Past visits Modal */}
        <EmployeePastVisitsModal
          open={open}
          handleClose={handleClose}
          data={selectedEmployee}
        />
      </Box>
    </React.Fragment>
  )
}

export default EmployeeInsights;
